<script setup lang="ts">
import { reactive, watch } from 'vue'
import {
  type UpdateRequisitionRequest,
  updateRequisitionAction,
} from '@/api/functions/requisitions/update-requisition-action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'

const props = defineProps({
  modelValue: Boolean,
  request: {
    type: Object,
    required: true,
  },
  currencies: {
    type: Array,
  },
  transferMethods: {
    type: Array,
  },
  countries: {
    type: Array,
  },
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const toast = useToast()

// --- Reactive form object ---
const form = reactive({
  amount: props.request?.currencyAmount || null,
  currency_id: props.request?.currency?.id || '',
  exchange_rate: props.request?.exchangeRate || null,
  country_id: props.request?.country?.id || '',
  transfer_method_ids: props.request?.transferMethods?.map((t: any) => t.id) || [],
  validity_days: props.request?.validityDays || null,
  description: props.request?.description || '',
})

const mutation = useMutationApi({
  mutationFn: (data: UpdateRequisitionRequest) => updateRequisitionAction(data),
  onSuccess: (res) => {
    toast.success(res.meta.message || 'درخواست با موفقیت به‌روزرسانی شد')
    closeModal()
    emit('confirm')
  },
  onError: (err: Error) => {
    toast.error(err?.message || 'خطا در بروزرسانی درخواست')
  },
})

const closeModal = () => {
  emit('update:modelValue', false)
  emit('cancel')
}

const confirmSave = () => {
  const payload: UpdateRequisitionRequest = {
    id: props.request.uuid,
    amount: form.amount,
    currency_id: form.currency_id,
    exchange_rate: form.exchange_rate,
    country_id: form.country_id,
    // transfer_method_ids: form.transfer_method_ids,
    validity_days: form.validity_days,
    description: form.description,
  }

  mutation.mutate(payload)
}

watch(
  () => props.request,
  (newRequest) => {
    form.amount = newRequest?.currencyAmount || null
    form.currency_id = newRequest?.currency?.id || ''
    form.exchange_rate = newRequest?.exchangeRate || null
    form.country_id = newRequest?.country?.id || ''
    form.transfer_method_ids = newRequest?.transferMethods?.map((t: { id: string }) => t.id) || []
    form.validity_days = newRequest?.validityDays
    form.description = newRequest?.description || ''
  },
  { immediate: true }
)
</script>

<template>
  <Modal :model-value="props.modelValue" @update:model-value="$emit('update:modelValue', $event)" width="600px"
    background="#FFF" :closable="true" title="ویرایش درخواست" closeDirection="left" class="update-request">
    <form @submit.prevent="confirmSave" class="flex flex-col min-h-[700px]">

      <div class="flex-1 overflow-y-auto px-4 py-6 pb-9">
        <Input v-model="form.amount" type="decimal" :decimal-places="2" name="volume" label="حجم" />

        <Select v-model="form.currency_id" label="ارز" :options="currencies" variant="checkbox" />

        <Input v-model="form.exchange_rate" label="نرخ تبدیل" type="number" />

        <Select v-model="form.country_id" label="کشور" :options="countries" variant="checkbox" />

        <!-- TODO  -->
        <Select v-model="form.transfer_method_ids" label="نوع انتقال" :options="transferMethods" variant="checkbox"
          :disabled="true" multiple />

        <Input v-model="form.validity_days" label="مدت زمان اعتبار درخواست" type="number" />

        <Textarea v-model="form.description" label="توضیحات" />
      </div>

      <div class="sticky bottom-0 bg-white z-50 flex justify-between gap-6 w-full pt-3 pb-9 px-2 border-gray-200">
        <Button type="submit" label="ذخیره تغییرات" :isAdmin="true" class="w-9/12 h-12 rounded-xl" />
        <Button type="button" @click="closeModal" variant="outline" label="بازگشت" :isAdmin="true"
          class="w-4/12 h-12 rounded-xl border-2" />
      </div>
    </form>
  </Modal>
</template>
